import React from 'react';
import {
  Pencil, Calendar, CalendarPlus, Clock, User, Users, UserPlus, Contact, Info, Search, Lock,
  Eye, EyeOff, Mail, Phone, Building2, Plus, Check, X, Trash2, Send, Upload, Download, Tag,
  MapPin, BookOpen, House, KeyRound, Globe, List, ShieldCheck, File, RefreshCw, Save,
  ArrowLeft, ArrowRight, CircleAlert, IndianRupee,
} from 'lucide-react';

// ── Shared design tokens (teal / lime, grey shell + inner card) ──────────────
export const C = {
  teal:       '#115550',
  tealMid:    '#16706a',
  tealLight:  '#c6e3e0',
  lime:       '#c8e94b',
  limeDim:    'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white:      '#ffffff',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  danger:     '#dc2626',
};

export const G = {
  shellBg:     '#f8f9f9',
  shellBorder: '#e8ebeb',
  line:        '#e5e7eb',
  soft:        '#eef0f0',
  softer:      '#f2f4f4',
};

export const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};

export const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Icon resolver ─────────────────────────────────────────────────────────────
// Accepts a lucide component (icon={Search}), a ready element (icon={<Search />}),
// or a legacy PrimeIcons class string (icon="pi pi-search") so old call sites keep working.
export type IconProp = string | React.ReactElement | React.ElementType;

const PI_MAP: Record<string, React.ElementType> = {
  'pencil': Pencil, 'calendar': Calendar, 'calendar-plus': CalendarPlus, 'clock': Clock,
  'user': User, 'users': Users, 'user-plus': UserPlus, 'id-card': Contact,
  'info-circle': Info, 'search': Search, 'lock': Lock, 'eye': Eye, 'eye-slash': EyeOff,
  'envelope': Mail, 'phone': Phone, 'building': Building2, 'plus': Plus, 'check': Check,
  'times': X, 'trash': Trash2, 'send': Send, 'upload': Upload, 'download': Download,
  'tag': Tag, 'map-marker': MapPin, 'book': BookOpen, 'home': House, 'key': KeyRound,
  'globe': Globe, 'list': List, 'shield': ShieldCheck, 'file': File, 'refresh': RefreshCw,
  'save': Save, 'arrow-left': ArrowLeft, 'arrow-right': ArrowRight,
  'exclamation-circle': CircleAlert, 'dollar': IndianRupee,
};

export const resolveIcon = (icon: IconProp | undefined, size = 14): React.ReactNode => {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === 'string') {
    const key = icon.split(/\s+/).find(c => c.startsWith('pi-'))?.slice(3);
    const Cmp = key ? PI_MAP[key] : undefined;
    return Cmp ? React.createElement(Cmp, { size }) : null;
  }
  return React.createElement(icon as React.ElementType, { size });
};
